import { Button, Dialog } from '@artosera/ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

export interface GardeSaisieProps {
  /** Vrai tant que des modifications ne sont pas enregistrées. */
  actif: boolean;
}

/**
 * Garde d'une saisie non enregistrée : quitter la page (lien, retour, fermeture
 * de l'onglet) avec des modifications en cours demande confirmation, au lieu de
 * les perdre en silence. Se pose dans une fiche ou un formulaire, à côté du
 * contenu : `<GardeSaisie actif={modifie} />`.
 */
export function GardeSaisie({ actif }: GardeSaisieProps): JSX.Element | null {
  const { t } = useTranslation(['regie', 'commun']);
  const blocage = useBlocker(
    ({ currentLocation, nextLocation }) =>
      actif && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!actif) return undefined;
    function avertir(event: BeforeUnloadEvent): void {
      event.preventDefault();
    }
    window.addEventListener('beforeunload', avertir);
    return () => window.removeEventListener('beforeunload', avertir);
  }, [actif]);

  // Le blocage a pu naître juste avant que la saisie soit enregistrée : on le
  // lève plutôt que de laisser un dialogue sans objet.
  useEffect(() => {
    if (!actif && blocage.state === 'blocked') blocage.proceed();
  }, [actif, blocage]);

  if (blocage.state !== 'blocked') return null;
  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert) blocage.reset();
      }}
      title={t('regie:garde.titre')}
      description={t('regie:garde.texte')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={() => blocage.reset()}>
            {t('regie:garde.rester')}
          </Button>
          <Button variant="danger" onClick={() => blocage.proceed()}>
            {t('regie:garde.quitter')}
          </Button>
        </>
      }
    />
  );
}
